import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription } from "../components/ui/alert";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Poll Stripe checkout session status until paid / expired, then redirect
// the user to their Order Portal (magic-link secured).
const StripeSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [message, setMessage] = useState("Confirming your payment with Stripe…");
  const [orderId, setOrderId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setMessage("Missing Stripe session reference.");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        const { payment_status, status, order_id, magic_link } = res.data;
        if (order_id) setOrderId(order_id);

        if (payment_status === "paid") {
          setState("paid");
          setMessage("Payment successful! Redirecting to your order…");
          setTimeout(() => {
            if (magic_link) {
              try {
                const u = new URL(magic_link);
                navigate(u.pathname + u.search);
              } catch {
                navigate(`/orders/${order_id}`);
              }
            } else if (order_id) {
              navigate(`/orders/${order_id}`);
            }
          }, 1500);
          return;
        }
        if (status === "expired") {
          setState("expired");
          setMessage("This checkout session has expired. Please try again.");
          return;
        }

        if (attempts.current >= 10) {
          setState("error");
          setMessage("Payment status check timed out. Please check your email for confirmation, or visit your order.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (attempts.current >= 5) {
          setState("error");
          setMessage(err.response?.data?.detail || "Unable to verify payment status.");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg" data-testid="stripe-success-card">
        <CardContent className="p-8 text-center space-y-4">
          {state === "polling" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-brand animate-spin" />
              <h1 className="font-heading font-bold text-2xl text-primary">Confirming your payment</h1>
              <p className="text-muted-foreground">{message}</p>
              <p className="text-xs text-muted-foreground">This usually takes 3–5 seconds.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle className="h-12 w-12 mx-auto text-green-600" />
              <h1 className="font-heading font-bold text-2xl text-primary" data-testid="payment-success-title">Payment Confirmed</h1>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}
          {state === "expired" && (
            <>
              <Alert variant="destructive" className="text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <Link to="/courses">
                <Button className="bg-brand hover:bg-brand-dark text-white">
                  Back to Courses <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <Alert variant="destructive" className="text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              {orderId ? (
                <Link to={`/orders/${orderId}`}>
                  <Button className="bg-brand hover:bg-brand-dark text-white">
                    View My Order <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <Link to="/courses">
                  <Button className="bg-brand hover:bg-brand-dark text-white">
                    Back to Courses <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeSuccess;
